import { IoPerson, IoMail } from "react-icons/io5";
import { BsFillTelephoneFill, BsReceipt } from "react-icons/bs";

/**
 * Configuration for the category-driven contact form.
 *
 * IMPORTANT: The fields/required rules here are intentionally duplicated on the
 * backend in backend/configs/contact-categories.js. Keep the two in sync — the
 * backend is the source of truth for validation, this side mirrors it for
 * rendering and the client-side submit guard.
 */

// Single definition per field, reused across categories. The field's name is
// its key in this object.
export const FIELD_DEFS = {
  contactPerson: { type: "text", placeholderKey: "name", icon: IoPerson },
  phoneNumber: { type: "text", placeholderKey: "phone_number", icon: BsFillTelephoneFill },
  email: { type: "text", placeholderKey: "email", icon: IoMail },
  orderNumber: { type: "text", placeholderKey: "order_number", icon: BsReceipt },
  message: { type: "textarea", placeholderKey: "message" },
  cancelOrder: { type: "checkbox", placeholderKey: "cancel_order_checkbox" },
};

// The category's key is its key in this object.
// `labelKey`  — selector option label.
// `headingKey`/`infoKey` — ContactPage heading + description (selectable categories).
// `submitLabelKey` (optional) — overrides the submit button label (defaults to "send").
export const CONTACT_CATEGORIES = {
  general: {
    labelKey: "contact_category_general",
    headingKey: "feedback_placeholder",
    infoKey: "send_feedback_info",
    selectable: true,
    fields: ["contactPerson", "phoneNumber", "email", "message"],
    required: ["message"],
  },
  cancel_purchase: {
    labelKey: "contact_category_cancel_purchase",
    headingKey: "cancel_purchase_heading",
    infoKey: "cancel_purchase_info",
    selectable: true,
    fields: ["contactPerson", "phoneNumber", "email", "orderNumber", "message", "cancelOrder"],
    required: ["orderNumber", "cancelOrder", "message"],
  },
  // Slim, fixed category used by the shop pages. Never shown in the selector.
  product_inquiry: {
    labelKey: "contact_category_product_inquiry",
    selectable: false,
    fields: ["email", "message"],
    required: ["message"],
  },
};

// Default (empty) value for every possible field. Used to seed/reset form state.
export const EMPTY_FORM = Object.keys(FIELD_DEFS).reduce((acc, name) => {
  acc[name] = FIELD_DEFS[name].type === "checkbox" ? false : "";
  return acc;
}, {});

export const getSelectableCategories = () =>
  Object.keys(CONTACT_CATEGORIES).filter((key) => CONTACT_CATEGORIES[key].selectable);

/**
 * Whether a field may be pre-filled from the URL. Only known text-style fields
 * qualify — checkboxes (e.g. confirmation) must be ticked deliberately, never
 * pre-checked from a link.
 */
export const isFieldPrefillable = (fieldName) => {
  const def = FIELD_DEFS[fieldName];
  return !!def && def.type !== "checkbox";
};

/**
 * Mirrors the backend validation. Returns true when every required field for
 * the given category is satisfied.
 */
export const isCategoryFormValid = (categoryKey, form) => {
  const category = CONTACT_CATEGORIES[categoryKey];
  if (!category) {
    return false;
  }

  return category.required.every((fieldName) => {
    const def = FIELD_DEFS[fieldName];
    const value = form[fieldName];
    if (def && def.type === "checkbox") {
      return value === true;
    }
    return typeof value === "string" && value.trim() !== "";
  });
};
